// Sample Tracking Service - connects to Postgres via Azure Function
import { authService } from '../auth/authService';
import { authenticatedFetch } from './authenticatedFetch';
import {
  buildTypeByWorkorder,
  holeHasWorkorderTypes,
  isQaqcRelevantWorkorderType,
  sortWorkorderDetailsByType,
} from '../utils/sampleTrackingWorkorderTypes';
import { computeColmatchDisplayStatus } from '../utils/sampleTrackingColmatchStatus';

class SampleTrackingService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
    // Azure Function endpoints for sample tracking
    this.collarLocationsUrl = `${this.functionAppUrl}/api/collar_locations`;
    this.resultProgressUrl = `${this.functionAppUrl}/api/result_progress`;
    this.workorderProgressUrl = `${this.functionAppUrl}/api/workorder_progress`;
    this.workorderQaqcStatusUrl = `${this.functionAppUrl}/api/workorder_qaqc_status`;
    this.alsSyncLogUrl = `${this.functionAppUrl}/api/als_sync_log`;
    this.syncQaqcStatusUrl = `${this.functionAppUrl}/api/sync_qaqc_status`;
    this.sampleLocationUrl = `${this.functionAppUrl}/api/sample_location`;
    this.sampleTrackingUrl = `${this.functionAppUrl}/api/sample_tracking`;
    this.holesColmatchUrl = `${this.functionAppUrl}/api/holes_colmatch`;
    this.holesUnresolvedFailuresUrl = `${this.functionAppUrl}/api/holes_unresolved_failures`;
    this.labApprovalCostMetricsUrl = `${this.functionAppUrl}/api/lab_approval_cost_metrics`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken();
      const userInfo = authService.getUserInfo();
      
      // Get user's group memberships for authorization
      const userGroups = await authService.getUserGroups();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
        'x-user-groups': JSON.stringify(userGroups) // Pass group IDs as JSON array
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Get collar location data for drill holes (API reads mv_news_release_collar_locations)
   */
  async getCollarLocations() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.collarLocationsUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch collar locations: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching collar locations:', error);
      throw error;
    }
  }

  /**
   * Get result progress data (QAQC results and news release info)
   */
  async getResultProgress() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.resultProgressUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch result progress: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching result progress:', error);
      throw error;
    }
  }

  /**
   * Get sample tracking data (hole-level news_release_date from mv_drilling_progress_metrics)
   */
  async getSampleTracking() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.sampleTrackingUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sample tracking: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📊 Sample tracking data retrieved:', result.count || result.data?.length || 0, 'records');
      return result.data || [];
    } catch (error) {
      console.error('Error fetching sample tracking:', error);
      throw error;
    }
  }

  /**
   * Weighted avg cost per analysis sample by workorder type and campaign (approved lab approvals only).
   * @returns {Promise<object|null>}
   */
  async getLabApprovalCostMetrics() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.labApprovalCostMetricsUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`lab_approval_cost_metrics ${response.status} ${text}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || result.error || 'lab_approval_cost_metrics failed');
      }
      return result;
    } catch (error) {
      console.error('Error fetching lab approval cost metrics:', error);
      throw error;
    }
  }

  /**
   * Get workorder progress data
   */
  async getWorkorderProgress() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.workorderProgressUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workorder progress: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching workorder progress:', error);
      throw error;
    }
  }

  /**
   * Get workorder QAQC status data
   * Returns detailed workorder status with QAQC info and sample depths
   */
  async getWorkorderQaqcStatus() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.workorderQaqcStatusUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        console.warn(`⚠️ Workorder QAQC status API returned ${response.status}.`);
        return [];
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.warn('⚠️ Workorder QAQC status API unavailable.');
      return [];
    }
  }

  /**
   * Hole-level ORIG sample colmatch (expected vs passed) from minto_qaqc
   */
  async getHolesColmatch() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.holesColmatchUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.warn(`⚠️ Holes colmatch API returned ${response.status}.`);
        return [];
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.warn('⚠️ Holes colmatch API unavailable.');
      return [];
    }
  }

  normalizeColmatchHolePassed(val) {
    if (val === true || val === 1) return true;
    if (val === false || val === 0) return false;
    if (val == null || val === '') return null;
    const s = String(val).trim().toLowerCase();
    if (s === 'true' || s === 't' || s === '1') return true;
    if (s === 'false' || s === 'f' || s === '0') return false;
    return null;
  }

  applyColmatchToHole(hole, colmatchRow) {
    if (!colmatchRow) return hole;
    return {
      ...hole,
      colmatch_expected_sample_count: colmatchRow.expected_sample_count ?? null,
      colmatch_passed_sample_count: colmatchRow.passed_sample_count ?? null,
      colmatch_missing_passed_sample_count:
        colmatchRow.missing_passed_sample_count ?? null,
      colmatch_hole_passed: this.normalizeColmatchHolePassed(colmatchRow.hole_passed),
      colmatch_fail_reason: colmatchRow.fail_reason ?? null,
      colmatch_has_data: true,
    };
  }

  async getHolesUnresolvedFailures() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.holesUnresolvedFailuresUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Holes unresolved failures API returned ${response.status}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.warn('⚠️ Holes unresolved failures API unavailable.', error.message);
      throw error;
    }
  }

  applyColmatchDisplayStatus(hole, unresolvedFailuresForHole, failuresApiOk) {
    const enriched = {
      ...hole,
      colmatch_unresolved_failures: unresolvedFailuresForHole || [],
      colmatch_unresolved_failures_api_ok: failuresApiOk,
    };
    const colmatch_display_status = computeColmatchDisplayStatus(enriched);
    return {
      ...enriched,
      colmatch_display_status,
      colmatch_display_label: colmatch_display_status.label,
    };
  }

  getQaqcBadgeClassFromArray(qaqcArray) {
    if (!qaqcArray || qaqcArray.length === 0) return 'qaqc-unknown';

    const allPassed = qaqcArray.every((q) => q === 't');
    const anyFailed = qaqcArray.some((q) => q === 'f');

    if (allPassed) return 'qaqc-passed';
    if (anyFailed) return 'qaqc-failed';
    return 'qaqc-mixed';
  }

  /**
   * QAQC badge class; red colmatch override when all workorders pass but hole colmatch fails.
   */
  getQaqcBadgeClassForHole(hole, qaqcArray) {
    const baseClass = this.getQaqcBadgeClassFromArray(qaqcArray);
    const allWoPassed =
      qaqcArray?.length > 0 && qaqcArray.every((q) => q === 't');
    if (allWoPassed && hole?.colmatch_hole_passed === false) {
      return 'qaqc-colmatch-failed';
    }
    return baseClass;
  }

  /**
   * Get ALS sync log - last sync or full history
   * @param {Object} params - Query parameters (limit, status, sync_type, start_date, end_date)
   */
  async getAlsSyncLog(params = {}) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      
      // Build query string
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);
      if (params.sync_type) queryParams.append('sync_type', params.sync_type);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.execution_id) queryParams.append('execution_id', params.execution_id);
      
      const url = `${this.alsSyncLogUrl}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sync log: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching sync log:', error);
      throw error;
    }
  }

  /**
   * Get last sync timestamp (most recent sync)
   */
  async getLastSyncTimestamp() {
    try {
      const result = await this.getAlsSyncLog({ limit: 1 });
      if (result.success && result.data && result.data.length > 0) {
        return result.data[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching last sync:', error);
      return null;
    }
  }

  /**
   * Sync QAQC status from minto_qaqc to minto_db
   * This ensures the drilling_progress database has up-to-date QAQC data
   */
  async syncQaqcStatus() {
    try {
      console.log('🔄 Triggering QAQC status sync...');
      const headers = await this.getAuthenticatedHeaders();
      
      const response = await authenticatedFetch(this.syncQaqcStatusUrl, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ QAQC sync completed:', result);
      return result;
    } catch (error) {
      console.error('⚠️ QAQC sync failed (non-critical):', error);
      // Don't throw - sync failure shouldn't prevent dashboard from loading
      return { success: false, error: error.message };
    }
  }

  /**
   * Parse comma-separated ordered lists into arrays
   * Returns empty array for null/undefined/empty values
   */
  parseOrderedList(value) {
    if (!value || value === 'N/A' || value === '' || value === 'null') return [];
    
    try {
      return value.split(',').map(item => item.trim()).filter(item => item !== '');
    } catch (error) {
      console.warn('Error parsing ordered list:', value, error);
      return [];
    }
  }

  /**
   * Normalize QAQC pass/fail from API (mv may use "true"/"false"; older data used "t"/"f").
   * Returns 't', 'f', or null for unknown/empty.
   */
  normalizeQaqcToken(val) {
    if (val === true || val === 1) return 't';
    if (val === false || val === 0) return 'f';
    if (val == null || val === '') return null;
    const s = String(val).trim().toLowerCase();
    if (s === 't' || s === 'true' || s === '1' || s === 'yes' || s === 'y') return 't';
    if (s === 'f' || s === 'false' || s === '0' || s === 'no' || s === 'n') return 'f';
    return null;
  }

  /**
   * Format a depth segment from mv_drilling_progress_metrics.sample_depths
   * e.g. "90.000000-152.000000" -> "90-152", "318.500000-408.000000" -> "318.5-408"
   */
  formatMetricsDepthRange(segment) {
    if (!segment || typeof segment !== 'string') return '';
    const trimmed = segment.trim();
    const m = trimmed.match(/^([\d.]+)\s*-\s*([\d.]+)/);
    if (!m) return trimmed;
    const lo = parseFloat(m[1]);
    const hi = parseFloat(m[2]);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return trimmed;
    return `${lo}-${hi}`;
  }

  /**
   * Depth and sample counts from drilling_progress.mv_drilling_progress_metrics,
   * aligned to comma-separated workorder_numbers on the same row.
   */
  buildMetricsWorkorderExtras(sampleTrackingInfo) {
    const depthByWorkorder = new Map();
    const sampleCountByWorkorder = new Map();
    if (!sampleTrackingInfo) {
      return { depthByWorkorder, sampleCountByWorkorder };
    }

    const woList = this.parseOrderedList(sampleTrackingInfo.workorder_numbers);
    const depthList = this.parseOrderedList(sampleTrackingInfo.sample_depths || '');
    const countList = this.parseOrderedList(
      sampleTrackingInfo.workorder_sample_counts != null && sampleTrackingInfo.workorder_sample_counts !== ''
        ? String(sampleTrackingInfo.workorder_sample_counts)
        : ''
    );

    woList.forEach((woNum, i) => {
      if (!woNum) return;
      if (depthList[i]) {
        depthByWorkorder.set(woNum, this.formatMetricsDepthRange(depthList[i]));
      }
      if (countList[i] !== undefined && countList[i] !== '') {
        const n = parseInt(countList[i], 10);
        if (!Number.isNaN(n)) {
          sampleCountByWorkorder.set(woNum, n);
        }
      }
    });

    return { depthByWorkorder, sampleCountByWorkorder };
  }

  /**
   * Parse workorder_depths field
   * Format: "25SCM013 64-142" → { hole_id: "25SCM013", depth_range: "64-142" }
   * Handles semicolons and other trailing characters
   */
  parseWorkorderDepths(depthsString) {
    if (!depthsString || depthsString.trim() === '') {
      return { hole_id: null, depth_range: null };
    }

    try {
      const parts = depthsString.trim().split(' ');
      if (parts.length >= 2) {
        // Clean depth range: remove semicolons and other non-numeric/dash characters at the end
        let depthRange = parts[1].replace(/[;,]m?$/g, ''); // Remove trailing semicolons, commas before optional 'm'
        return {
          hole_id: parts[0],
          depth_range: depthRange
        };
      }
      return { hole_id: depthsString, depth_range: null };
    } catch (error) {
      console.warn('Error parsing workorder depths:', depthsString, error);
      return { hole_id: null, depth_range: null };
    }
  }

  /**
   * Create a workorder status map from QAQC data
   * This is the "source of truth" for workorder statuses
   */
  createWorkorderStatusMap(qaqcData) {
    const map = new Map();
    
    qaqcData.forEach(wo => {
      if (wo.workorder_id) {
        map.set(wo.workorder_id, {
          status: wo.status,
          sample_count: wo.sample_count,
          has_unresolved_failure: wo.has_unresolved_failure,
          qaqc_passed: wo.qaqc_passed,
          workorder_depths: wo.workorder_depths,
          ...this.parseWorkorderDepths(wo.workorder_depths)
        });
      }
    });
    
    return map;
  }

  /**
   * Parse final ETA dates from various formats
   * Handles: "2026-04-29", "17-Nov (Cu Overlimit)", "16-Mar", "Mar 30", "Apr 5", "WH prepped+shipped", etc.
   */
  parseFinalEta(etaString) {
    if (!etaString) return [];
    
    const monthMap = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    const parts = etaString.split(',').map(item => item.trim());
    const parsed = [];
    const now = new Date();
    
    parts.forEach(part => {
      let date = null;
      let display = part;

      // Format 0: "2026-04-29" (ISO YYYY-MM-DD)
      const isoMatch = part.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const y = parseInt(isoMatch[1]);
        const m = parseInt(isoMatch[2]) - 1;
        const d = parseInt(isoMatch[3]);
        date = new Date(y, m, d);
        display = `${monthNames[m]} ${d}`;
      }

      if (!date) {
        let day, month;

        // Format 1: "16-Mar", "6-Dec", "17-Nov (Cu Overlimit)"
        const ddMonMatch = part.match(/(\d{1,2})-([A-Za-z]{3})/);
        // Format 2: "Mar 30", "Apr 5"
        const monDdMatch = part.match(/([A-Za-z]{3})\s+(\d{1,2})/);

        if (ddMonMatch) {
          day = parseInt(ddMonMatch[1]);
          month = ddMonMatch[2];
        } else if (monDdMatch && monthMap[monDdMatch[1]] !== undefined) {
          month = monDdMatch[1];
          day = parseInt(monDdMatch[2]);
        }

        if (day && month && monthMap[month] !== undefined) {
          let year = now.getFullYear();
          const candidate = new Date(year, monthMap[month], day);
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          if (candidate < sixMonthsAgo) {
            year += 1;
          }
          date = new Date(year, monthMap[month], day);
        }
      }

      if (date) {
        parsed.push({
          raw: part,
          date: date,
          sortable: date.getTime(),
          display: display
        });
      } else {
        parsed.push({
          raw: part,
          date: null,
          sortable: Infinity,
          display: part
        });
      }
    });
    
    return parsed;
  }

  /**
   * Get earliest date from final_eta for sorting
   */
  getEarliestEta(etaString) {
    const parsed = this.parseFinalEta(etaString);
    if (parsed.length === 0) return Infinity;
    
    const dates = parsed.filter(p => p.date !== null);
    if (dates.length === 0) return Infinity;
    
    return Math.min(...dates.map(d => d.sortable));
  }

  /**
   * Merge data from all APIs by hole_id
   * Reconciles workorder statuses using QAQC data as source of truth
   */
  mergeHoleData(collarData, resultData, workorderData, workorderQaqcStatusMap, sampleTrackingMap) {
    const mergedMap = new Map();

    // Start with result progress data (main dataset)
    resultData.forEach(result => {
      const holeId = result.hole_id;
      
      // Skip if no hole_id
      if (!holeId) {
        console.warn('Skipping result with no hole_id:', result);
        return;
      }
      
      // Parse workorder numbers, statuses, and QAQC data
      const workorderNumbers = this.parseOrderedList(result.workorder_numbers);
      let workorderStatuses = this.parseOrderedList(result.workorder_statuses);
      const qaqcPassed = this.parseOrderedList(result.qaqc_passed_ordered);
      
      // DEDUPLICATE workorder numbers (same workorder can appear multiple times)
      // Keep track of the first index for each unique workorder for QAQC mapping
      const uniqueWorkorders = new Map();
      workorderNumbers.forEach((woNum, index) => {
        if (!uniqueWorkorders.has(woNum)) {
          uniqueWorkorders.set(woNum, {
            workorder_id: woNum,
            original_status: workorderStatuses[index] || 'Unknown',
            qaqc_passed: this.normalizeQaqcToken(qaqcPassed[index]),
            index: index
          });
        }
      });
      
      // Get unique workorder numbers and deduplicated QAQC array
      const uniqueWorkorderNumbers = Array.from(uniqueWorkorders.keys());
      const deduplicatedQaqc = uniqueWorkorderNumbers.map(woNum =>
        uniqueWorkorders.get(woNum).qaqc_passed
      );
      
      // Reconcile statuses with QAQC data (QAQC data is source of truth)
      const reconciledStatuses = uniqueWorkorderNumbers.map(woNum => {
        const qaqcData = workorderQaqcStatusMap?.get(woNum);
        const originalStatus = uniqueWorkorders.get(woNum).original_status;
        // If QAQC data exists, use its status; otherwise use original
        return qaqcData ? qaqcData.status : originalStatus;
      });
      
      // Build workorder details array (deduplicated)
      const workorderDetails = uniqueWorkorderNumbers.map((woNum, index) => {
        const qaqcData = workorderQaqcStatusMap?.get(woNum);
        return {
          workorder_id: woNum,
          status: reconciledStatuses[index],
          sample_count: qaqcData?.sample_count,
          has_unresolved_failure: qaqcData?.has_unresolved_failure,
          qaqc_passed: qaqcData?.qaqc_passed,
          depth_range: qaqcData?.depth_range,
          workorder_depths: qaqcData?.workorder_depths
        };
      });
      
      // Get news_release_date and workorder_cpts from sample tracking map
      const sampleTrackingInfo = sampleTrackingMap?.get(holeId);
      const newsReleaseDate = sampleTrackingInfo?.news_release_date || null;

      // Parse workorder_cpts (comma-separated, aligned to workorder_numbers from sample_tracking)
      const workorderCpts = this.parseOrderedList(sampleTrackingInfo?.workorder_cpts);
      const sampleTrackingWoNumbers = this.parseOrderedList(sampleTrackingInfo?.workorder_numbers);
      const cptByWorkorder = new Map();
      sampleTrackingWoNumbers.forEach((woNum, i) => {
        if (!cptByWorkorder.has(woNum) && workorderCpts[i]) {
          cptByWorkorder.set(woNum, workorderCpts[i]);
        }
      });
      workorderDetails.forEach(wo => {
        wo.cpt = cptByWorkorder.get(wo.workorder_id) || null;
      });

      // Depth ranges and per-workorder sample counts from mv_drilling_progress_metrics (aligned lists)
      const metricsExtras = this.buildMetricsWorkorderExtras(sampleTrackingInfo);
      workorderDetails.forEach(wo => {
        const mDepth = metricsExtras.depthByWorkorder.get(wo.workorder_id);
        if (mDepth) {
          wo.depth_range = mDepth;
        }
        const mCount = metricsExtras.sampleCountByWorkorder.get(wo.workorder_id);
        if (mCount != null) {
          wo.sample_count = mCount;
        }
      });

      const typeByWorkorder = buildTypeByWorkorder(
        sampleTrackingInfo,
        (value) => this.parseOrderedList(value)
      );
      workorderDetails.forEach((wo) => {
        wo.workorder_type = typeByWorkorder.get(wo.workorder_id) ?? null;
      });

      const finalEtaRawList = this.parseOrderedList(result.final_eta);
      workorderDetails.forEach((wo) => {
        const idx = uniqueWorkorders.get(wo.workorder_id)?.index;
        const raw =
          idx != null && idx < finalEtaRawList.length ? finalEtaRawList[idx] : null;
        if (raw) {
          const parsedPart = this.parseFinalEta(raw)[0];
          wo.final_eta = raw;
          wo.final_eta_display = parsedPart?.display ?? raw;
        }
      });

      const qaqcByWorkorder = new Map();
      uniqueWorkorderNumbers.forEach((woNum, i) => {
        qaqcByWorkorder.set(woNum, deduplicatedQaqc[i]);
      });

      const sortedWorkorderDetails = sortWorkorderDetailsByType(workorderDetails);

      const hasTypes = holeHasWorkorderTypes(sortedWorkorderDetails);
      const qaqcAssayPassedArray = hasTypes
        ? sortedWorkorderDetails
            .filter((wo) => isQaqcRelevantWorkorderType(wo.workorder_type))
            .map((wo) => qaqcByWorkorder.get(wo.workorder_id) ?? null)
        : null;

      mergedMap.set(holeId, {
        hole_id: holeId,
        final_eta: result.final_eta || '',
        workorder_numbers: result.workorder_numbers || '',
        workorder_statuses: reconciledStatuses.join(', '), // Reconciled statuses (deduplicated)
        qaqc_passed_ordered: result.qaqc_passed_ordered || '',
        news_release_date_ordered: result.news_release_date_ordered || '',
        news_release_date: newsReleaseDate, // From mv_drilling_progress_metrics (sample_tracking API)
        priority: sampleTrackingInfo?.priority || null,
        expected_completion_date: sampleTrackingInfo?.expected_completion_date || null,
        // Parsed arrays (ALL DEDUPLICATED)
        workorder_numbers_array: uniqueWorkorderNumbers,
        workorder_statuses_array: reconciledStatuses, // Reconciled (deduplicated)
        qaqc_passed_array: deduplicatedQaqc, // DEDUPLICATED to match unique workorders
        qaqc_assay_passed_array: qaqcAssayPassedArray,
        news_release_dates_array: this.parseOrderedList(result.news_release_date_ordered),
        eta_parsed: this.parseFinalEta(result.final_eta),
        eta_earliest: this.getEarliestEta(result.final_eta),
        // Workorder details with QAQC info (deduplicated, sorted by workorder_type)
        workorder_details: sortedWorkorderDetails
      });
    });

    // Add collar location data
    collarData.forEach(collar => {
      const holeId = collar.hole_id || collar['Hole ID'];
      if (mergedMap.has(holeId)) {
        const existing = mergedMap.get(holeId);
        mergedMap.set(holeId, {
          ...existing,
          easting: collar.easting || collar['Easting'],
          northing: collar.northing || collar['Northing'],
          azi: collar.azi || collar['Azi'],
          dip: collar.dip || collar['Dip'],
          eoh: collar.eoh || collar['EOH']
        });
      }
    });

    return Array.from(mergedMap.values());
  }

  /**
   * Get all sample tracking dashboard data
   * Fetches collar locations, result progress, workorder progress, and workorder QAQC status
   * Uses workorder QAQC status as source of truth for reconciling statuses
   * Uses graceful degradation - continues even if some APIs fail
   */
  async getDashboardData() {
    try {
      // Fetch all APIs with individual error handling
      const [
        collarResult,
        resultResult,
        workorderResult,
        qaqcStatusResult,
        sampleTrackingResult,
        colmatchResult,
        unresolvedFailuresResult,
      ] = await Promise.allSettled([
        this.getCollarLocations().catch(err => {
          console.warn('⚠️ Collar locations API failed:', err.message);
          return [];
        }),
        this.getResultProgress().catch(err => {
          console.warn('⚠️ Result progress API failed:', err.message);
          return [];
        }),
        this.getWorkorderProgress().catch(err => {
          console.warn('⚠️ Workorder progress API failed:', err.message);
          return [];
        }),
        this.getWorkorderQaqcStatus().catch(err => {
          console.warn('⚠️ Workorder QAQC status API failed:', err.message);
          return [];
        }),
        this.getSampleTracking().catch(err => {
          console.warn('⚠️ Sample tracking API failed:', err.message);
          return [];
        }),
        this.getHolesColmatch().catch(err => {
          console.warn('⚠️ Holes colmatch API failed:', err.message);
          return [];
        }),
        this.getHolesUnresolvedFailures().catch(err => {
          console.warn('⚠️ Holes unresolved failures API failed:', err.message);
          return [];
        }),
      ]);

      // Extract data from settled promises
      const collarData = collarResult.status === 'fulfilled' ? collarResult.value : [];
      const resultData = resultResult.status === 'fulfilled' ? resultResult.value : [];
      const workorderData = workorderResult.status === 'fulfilled' ? workorderResult.value : [];
      const qaqcStatusData = qaqcStatusResult.status === 'fulfilled' ? qaqcStatusResult.value : [];
      const sampleTrackingData = sampleTrackingResult.status === 'fulfilled' ? sampleTrackingResult.value : [];
      const colmatchData = colmatchResult.status === 'fulfilled' ? colmatchResult.value : [];
      const unresolvedFailuresData =
        unresolvedFailuresResult.status === 'fulfilled'
          ? unresolvedFailuresResult.value
          : [];
      const unresolvedFailuresApiOk = unresolvedFailuresResult.status === 'fulfilled';

      console.log('📊 API Data Retrieved:', {
        collars: collarData.length,
        results: resultData.length,
        workorders: workorderData.length,
        qaqcStatus: qaqcStatusData.length,
        sampleTracking: sampleTrackingData.length,
        colmatch: colmatchData.length,
        unresolvedFailures: unresolvedFailuresData.length,
      });

      // Throw error if no data at all
      if (collarData.length === 0 && resultData.length === 0) {
        throw new Error('Unable to load any data. Please check your connection and permissions.');
      }

      // Create workorder status map from QAQC data (source of truth)
      const workorderStatusMap = this.createWorkorderStatusMap(qaqcStatusData);
      console.log('📊 Workorder status map created:', workorderStatusMap.size, 'workorders');

      // Create sample tracking map for news_release_date (source of truth for hole-level release date)
      const sampleTrackingMap = new Map();
      sampleTrackingData.forEach(sample => {
        if (sample.hole_id) {
          sampleTrackingMap.set(sample.hole_id, {
            news_release_date: sample.news_release_date,
            workorder_cpts: sample.workorder_cpts,
            workorder_numbers: sample.workorder_numbers,
            sample_depths: sample.sample_depths,
            workorder_sample_counts: sample.workorder_sample_counts,
            workorder_types: sample.workorder_types ?? sample.workorder_type,
            priority: sample.priority,
            expected_completion_date: sample.expected_completion_date
          });
        }
      });
      console.log('📊 Sample tracking map created:', sampleTrackingMap.size, 'holes');

      // Merge all data by hole_id with reconciled statuses
      let mergedData = this.mergeHoleData(collarData, resultData, workorderData, workorderStatusMap, sampleTrackingMap);

      const colmatchMap = new Map();
      colmatchData.forEach((row) => {
        if (row.hole_id) {
          colmatchMap.set(row.hole_id, row);
        }
      });
      mergedData = mergedData.map((hole) =>
        this.applyColmatchToHole(hole, colmatchMap.get(hole.hole_id))
      );

      const failuresByHole = new Map();
      unresolvedFailuresData.forEach((row) => {
        if (!row.hole_id) return;
        if (!failuresByHole.has(row.hole_id)) {
          failuresByHole.set(row.hole_id, []);
        }
        failuresByHole.get(row.hole_id).push(row);
      });

      mergedData = mergedData.map((hole) =>
        this.applyColmatchDisplayStatus(
          hole,
          failuresByHole.get(hole.hole_id) || [],
          unresolvedFailuresApiOk
        )
      );

      // Calculate summary metrics
      const summary = {
        total_holes: mergedData.length,
        total_workorders: mergedData.reduce((sum, hole) => 
          sum + (hole.workorder_numbers_array?.length || 0), 0),
        active_workorders: mergedData.reduce((sum, hole) => 
          sum + (hole.workorder_statuses_array?.filter(s => s === 'Active').length || 0), 0),
        finalized_workorders: mergedData.reduce((sum, hole) => 
          sum + (hole.workorder_statuses_array?.filter(s => s === 'Finalized').length || 0), 0),
        holes_with_qaqc_passed: mergedData.filter(hole => 
          hole.qaqc_passed_array?.length > 0 && hole.qaqc_passed_array.every(q => q === 't')).length
      };

      return {
        data: mergedData,
        summary: summary,
        success: true,
        warnings: {
          collarsFailed: collarResult.status === 'rejected',
          resultsFailed: resultResult.status === 'rejected',
          workordersFailed: workorderResult.status === 'rejected',
          qaqcStatusFailed: qaqcStatusResult.status === 'rejected',
          sampleTrackingFailed: sampleTrackingResult.status === 'rejected',
          colmatchFailed: colmatchResult.status === 'rejected',
          unresolvedFailuresFailed: unresolvedFailuresResult.status === 'rejected',
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Filter by hole_id OR workorder number
   * By default (no searchTerm), shows ALL data
   */
  filterData(data, searchTerm) {
    // Show all data if no search term
    if (!searchTerm || searchTerm.trim() === '') return data;
    
    const term = searchTerm.toLowerCase().trim();
    
    return data.filter(hole => {
      // Search in hole_id
      if (hole.hole_id?.toLowerCase().includes(term)) {
        return true;
      }
      
      // Search in workorder numbers (raw string)
      if (hole.workorder_numbers?.toLowerCase().includes(term)) {
        return true;
      }
      
      // Search in workorder array
      if (hole.workorder_numbers_array?.some(wo => wo?.toLowerCase().includes(term))) {
        return true;
      }
      
      return false;
    });
  }

  /**
   * Sort data by field and direction
   */
  sortData(data, field, direction = 'asc') {
    return [...data].sort((a, b) => {
      let aVal, bVal;
      
      // Special handling for ETA field (use earliest date)
      if (field === 'final_eta') {
        aVal = a.eta_earliest;
        bVal = b.eta_earliest;
      } else {
        aVal = a[field] || '';
        bVal = b[field] || '';
      }
      
      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }

  /**
   * Filter by workorder status
   * By default (status='all'), shows ALL data including holes with no workorders
   */
  filterByStatus(data, status) {
    // Show all data if 'all' or no status filter
    if (!status || status === 'all') return data;
    
    // Filter by specific status, but keep holes with no status data
    return data.filter(hole => {
      // If no workorder statuses, keep the hole (show it)
      if (!hole.workorder_statuses_array || hole.workorder_statuses_array.length === 0) {
        return true;
      }
      // Check if any workorder matches the status
      return hole.workorder_statuses_array.some(s => 
        s && s.toLowerCase() === status.toLowerCase()
      );
    });
  }

  /**
   * Filter by QAQC status
   * By default (qaqcFilter='all'), shows ALL data including holes with no QAQC data
   */
  /** QAQC tokens for filters/table: assay-only when workorder types are known. */
  getQaqcArrayForHole(hole) {
    if (
      hole.qaqc_assay_passed_array &&
      holeHasWorkorderTypes(hole.workorder_details)
    ) {
      return hole.qaqc_assay_passed_array;
    }
    return hole.qaqc_passed_array;
  }

  filterByQaqc(data, qaqcFilter) {
    // Show all data if 'all' or no QAQC filter
    if (!qaqcFilter || qaqcFilter === 'all') return data;
    
    if (qaqcFilter === 'passed') {
      return data.filter(hole => {
        const qaqcArray = this.getQaqcArrayForHole(hole);
        // If no QAQC data, keep the hole (show it)
        if (!qaqcArray || qaqcArray.length === 0) {
          return true;
        }
        // Check if any QAQC passed
        return qaqcArray.some(q => q === 't');
      });
    } else if (qaqcFilter === 'failed') {
      return data.filter(hole => {
        const qaqcArray = this.getQaqcArrayForHole(hole);
        // If no QAQC data, keep the hole (show it)
        if (!qaqcArray || qaqcArray.length === 0) {
          return true;
        }
        // Check if any QAQC failed
        return qaqcArray.some(q => q === 'f');
      });
    }
    
    return data;
  }

  /**
   * Filter by news release date
   * Options: 'all', 'not_released', 'released', or specific date string
   */
  filterByNewsRelease(data, newsReleaseFilter) {
    // Show all data if 'all' or no filter
    if (!newsReleaseFilter || newsReleaseFilter === 'all') return data;
    
    if (newsReleaseFilter === 'not_released') {
      // Show only holes without a news release date (NULL or empty)
      return data.filter(hole => {
        return !hole.news_release_date || 
               hole.news_release_date === '' || 
               hole.news_release_date === 'null';
      });
    } else if (newsReleaseFilter === 'released') {
      // Show only holes with ANY news release date (NOT NULL)
      return data.filter(hole => {
        return hole.news_release_date && 
               hole.news_release_date !== '' && 
               hole.news_release_date !== 'null';
      });
    } else {
      // Filter by specific news release date
      return data.filter(hole => {
        return hole.news_release_date === newsReleaseFilter;
      });
    }
  }

  /**
   * Get sample location data
   * Shows where samples are in the processing pipeline
   */
  async getSampleLocation() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await authenticatedFetch(this.sampleLocationUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sample location: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📍 Sample location data retrieved:', result.count, 'records');
      return result.data || [];
    } catch (error) {
      console.error('Error fetching sample location:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const sampleTrackingService = new SampleTrackingService();
export default sampleTrackingService;

